/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { LogService, Logger } from '@epg/angular-base-lib';
import {
  faArrowLeft,
  faArrowRight,
  faImage,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faSpinnerThird,
  faUndo,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';
import { Subject, takeUntil } from 'rxjs';
import { SnackbarService } from '../../../services/snackbar.service';
import { ExifMetadataService } from '../exif/exif-metadata.service';

export interface EPGImageSliderSize {
  width?: number;
  height?: number;
  margin?: number;
}

export interface EPGImageSliderItem {
  image: string;
  imageFormat: EPGImageFormat;
  thumbImage?: string;
  title?: string;
  description?: string;
  rotationValue: number;
  scaleValue: number;
  zoomTopMargin?: number;
  imageLoadingStatus?: ImageLoadingStatus;
  exifData?: Record<string, any>;
}

export enum ImageLoadingStatus {
  LOADING = 'LOADING',
  LOADED = 'LOADED',
  ERROR = 'ERROR'
}

export enum EPGImageFormat {
  BASE64 = 'BASE64',
  URL = 'URL'
}
@Component({
  selector: 'epg-image-slider',
  templateUrl: './epg-image-slider.component.html',
  styleUrls: ['./epg-image-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class EpgImageSliderComponent implements OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();

  slideIndex = 0;
  faUndo = faUndo;
  faMagnifyingGlassPlus = faMagnifyingGlassPlus;
  faMagnifyingGlassMinus = faMagnifyingGlassMinus;
  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faDownload = faDownload;
  faXmark = faXmark;
  faSpinnerThird = faSpinnerThird;
  faImage = faImage;

  EPGImageFormat = EPGImageFormat;
  ImageLoadingStatus = ImageLoadingStatus;

  @ViewChildren('imageElement') imageElements: QueryList<ElementRef<HTMLElement>> | undefined;
  @ViewChildren('captionContainer') captionContainers:
    | QueryList<ElementRef<HTMLElement>>
    | undefined;

  @Input() images: EPGImageSliderItem[] = [];
  @Input() imageFormat: EPGImageFormat = EPGImageFormat.URL;
  @Input() imageHeightInPx?: number = 80;
  @Input() imageWidthInPx?: number = 200;
  @Input() respectOriginalAspectRatio? = false;
  @Input() disableLightBox? = false;
  @Input() displayBorder? = false;
  @Input() stretchToContainer = false;

  @Output() imageClick = new EventEmitter<number>();
  @Output() lightboxClose = new EventEmitter<object>();
  @Output() imageRotated = new EventEmitter<EPGImageSliderItem>();
  @ViewChild('lightbox') lightbox!: ElementRef;

  private log: Logger;

  constructor(
    private sanitizer: DomSanitizer,
    logService: LogService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private snackbarService: SnackbarService,
    private exifMetadataService: ExifMetadataService
  ) {
    this.log = logService.getLogger(this);
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.images) {
      for (const image of this.images) {
        if (image.image?.startsWith('http') && !image.imageLoadingStatus) {
          try {
            image.imageLoadingStatus = ImageLoadingStatus.LOADING;
            image.image = await this.getImageData(image.image);
            image.imageLoadingStatus = ImageLoadingStatus.LOADED;
          } catch (error) {
            image.imageLoadingStatus = ImageLoadingStatus.ERROR;
          }
        } else {
          image.imageLoadingStatus = ImageLoadingStatus.LOADED;
        }
        this.cdr.detectChanges();
      }
      // After all images loaded, recalculate captions
      setTimeout(() => {
        this.setCaptionsMaxWidth();
        this.cdr.detectChanges();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.setCaptionsMaxWidth();
  }

  async onImageLoad(index: number) {
    this.setCaptionMaxWidth(index);

    const image = this.images[index];
    if (!image || !image.image || image.exifData) {
      return;
    }

    try {
      if (image.imageFormat === EPGImageFormat.BASE64) {
        image.exifData = await this.exifMetadataService.readFromBase64(image.image);
      } else if (image.imageFormat === EPGImageFormat.URL) {
        const blobUrl = this.getBlobUrl(image.image);
        if (!blobUrl?.startsWith('blob:')) {
          return;
        }

        image.exifData = await this.exifMetadataService.readFromUrl(blobUrl, {
          assumeBlobUrl: true
        });
      }
    } catch (error) {
      this.log.warning('Failed to read EXIF metadata for image', error);
    }
  }

  private setCaptionsMaxWidth() {
    this.imageElements?.forEach((imageElement, index) => {
      this.setCaptionMaxWidth(index);
    });
  }

  //get the image width and apply that as the max-width to the caption
  private setCaptionMaxWidth(index: number) {
    const imageElement = this.imageElements?.toArray()[index];
    const captionContainer = this.captionContainers?.toArray()[index];
    if (imageElement && captionContainer) {
      const imageWidth = imageElement.nativeElement.offsetWidth;
      if (imageWidth > 0) {
        captionContainer.nativeElement.style.maxWidth = `${imageWidth}px`;
      }
    }
  }

  trackByImage(index: number, image: any): number {
    return index;
  }

  openModal(index: number) {
    if (this.disableLightBox) return;
    this.lightbox.nativeElement.style.display = 'block';
    this.slideIndex = index;
  }

  closeModal() {
    this.lightbox.nativeElement.style.display = 'none';
    this.images.forEach((image) => {
      image.scaleValue = 1;
      this.imageRotated.next(image);
      this.setImageZoomTopMargin(image);
    });
  }

  rotateSlides(index: number) {
    this.slideIndex += index;
    if (this.slideIndex >= this.images.length) {
      this.slideIndex = 0;
    }
    if (this.slideIndex < 0) {
      this.slideIndex = this.images.length - 1;
    }
    this.images.forEach((image) => {
      image.scaleValue = 1;
      this.imageRotated.next(image);
      this.setImageZoomTopMargin(image);
    });
  }

  rotateImage(image: EPGImageSliderItem) {
    image.rotationValue -= 90;
    if (image.rotationValue == -360) {
      image.rotationValue = 0;
    }
    image.scaleValue = 1;
    this.imageRotated.next(image);
    this.setImageZoomTopMargin(image);
  }

  sanitize(url: string) {
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : url;
  }

  getImageStyle(image: EPGImageSliderItem, index: number) {
    let transform = 'rotate(' + image.rotationValue + 'deg)';
    let height = '100%';
    // when the image rotated 90/270 digrees scale it up so that it will be filled in the parent div
    if (image.rotationValue % 180 != 0) {
      height = '150%';
    }
    this.setCaptionMaxWidth(index);

    return this.respectOriginalAspectRatio
      ? {
          transform: transform,
          width: this.imageHeightInPx + 'px'
        }
      : {
          transform: transform,
          height: height
        };
  }

  imageZoomOut(image: EPGImageSliderItem) {
    if (image.scaleValue > 1) {
      image.scaleValue = image.scaleValue - 1;
      this.imageRotated.next(image);
      this.setImageZoomTopMargin(image);
    }
  }

  imageZoomIn(image: EPGImageSliderItem) {
    if (image.scaleValue < 5) {
      image.scaleValue = image.scaleValue + 1;
      this.imageRotated.next(image);
      this.setImageZoomTopMargin(image);
    }
  }

  setImageZoomTopMargin(image: EPGImageSliderItem) {
    switch (image.scaleValue) {
      case 1:
        image.zoomTopMargin = 0;
        break;
      case 2:
        image.zoomTopMargin = 100;
        break;
      case 3:
        image.zoomTopMargin = 199;
        break;
      case 4:
        image.zoomTopMargin = 300;
        break;
      case 5:
        image.zoomTopMargin = 400;
        break;
    }
    this.imageRotated.next(image);
  }

  @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeModal();
    } else if (event.key === 'ArrowLeft') {
      this.rotateSlides(-1);
    } else if (event.key === 'ArrowRight') {
      this.rotateSlides(1);
    }
  }

  downloadImage(imageFile: EPGImageSliderItem) {
    try {
      const imageSource = imageFile.image;

      if (!imageSource) {
        return;
      }

      const fileType = this.getFileExtension(imageSource, imageFile.imageFormat);
      const fileName = 'image';
      const fullFileName = `${fileName}.${fileType}`;

      this.downloadFile(imageSource, fullFileName);
    } catch (error) {
      this.log.error('Error downloading image:', error);
      this.snackbarService.showSnackBar(
        $localize`:downloading image error message@@downloadingImageErrorMessage:Error in downloading image!`,
        '',
        'error'
      );
    }
  }
  private downloadFile(imageSource: string, fileName: string) {
    let link = document.createElement('a');
    document.body.appendChild(link);

    try {
      link.setAttribute('href', imageSource);
      link.setAttribute('download', fileName);
      link.dispatchEvent(new MouseEvent('click', { bubbles: false }));
    } finally {
      document.body.removeChild(link);
    }
  }

  private getFileExtension(imageUrl: string, imageFormat: EPGImageFormat): string {
    const defaultExtension = 'jpeg';

    if (!imageUrl) return defaultExtension;

    if (imageFormat === EPGImageFormat.BASE64) {
      const start = imageUrl.indexOf('/') + 1;
      const end = imageUrl.indexOf(';');
      return imageUrl.substring(start, end) || defaultExtension;
    }

    return defaultExtension;
  }

  private getBlobUrl(imageUrl: string): string | undefined {
    if (!imageUrl) {
      return undefined;
    }

    const blobIndex = imageUrl.indexOf('blob:');
    if (blobIndex === -1) {
      return imageUrl;
    }

    return imageUrl.substring(blobIndex);
  }

  // The backend returns 'application/octet-stream' instead of the correct MIME type (e.g. 'image/jpeg', 'image/png').
  // We detect the real image type from the magic bytes in the binary data.
  private async detectMimeType(blob: Blob): Promise<string> {
    const buffer = await blob.slice(0, 4).arrayBuffer();
    const bytes = new Uint8Array(buffer);

    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      return 'image/jpeg';
    }
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
      return 'image/png';
    }
    if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
      return 'image/gif';
    }

    return blob.type || 'image/jpeg';
  }

  getImageData(imageUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.http
        .get(imageUrl, { responseType: 'blob' })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async (data: Blob) => {
            const mimeType = await this.detectMimeType(data);
            const correctBlob = new Blob([data], { type: mimeType });
            resolve(URL.createObjectURL(correctBlob));
            setTimeout(() => {
              URL.revokeObjectURL(imageUrl);
            }, 1000);
          },
          error: (error) => {
            this.log.error('Error downloading image:', error);
            reject(error);
          }
        });
    });
  }
}
